import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, X, Calendar } from 'lucide-react';
import { parseISO } from 'date-fns';
import { Cruise } from '../context/CruiseContext';
import CustomCruiseIcon from './CustomCruiseIcon';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';

interface CruiseConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  conflictingCruise: Cruise;
  newCruiseDates: {
    departureDate: string;
    returnDate: string;
    shipName: string;
    cruiseLine: string;
  };
  cancelText?: string;
  confirmText?: string;
  zIndex?: number;
}

const CruiseConflictModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  conflictingCruise, 
  newCruiseDates,
  cancelText,
  confirmText,
  zIndex = 60,
}: CruiseConflictModalProps) => {
  const { formatDate } = useDisplayPreferences();
  
  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      style={{ zIndex }}
    >
      <motion.div 
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-orange-500 mr-3" />
            <h2 className="text-xl font-semibold text-gray-800">Date Conflict Detected</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
        <p className="text-gray-600">
          The dates for your new cruise overlap with an existing cruise reservation. 
          Please review the details below and decide how to proceed.
        </p>
        
        {/* New Cruise Details */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center mb-2">
            <CustomCruiseIcon size={34} className="text-blue-600 mr-2" />
            <h4 className="font-semibold text-blue-900">New Cruise</h4>
          </div>
          <div className="text-sm space-y-1">
            <p className="font-medium text-blue-800">{newCruiseDates.shipName}</p>
            <p className="text-blue-700">{newCruiseDates.cruiseLine}</p>
            <div className="flex items-center text-blue-600">
              <Calendar className="h-4 w-4 mr-1" />
              <span>
                {formatDate(parseISO(newCruiseDates.departureDate))} - {formatDate(parseISO(newCruiseDates.returnDate))}
              </span>
            </div>
          </div>
        </div>

        {/* Conflicting Cruise Details */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex items-center mb-2">
            <AlertTriangle className="h-5 w-5 text-orange-600 mr-2" />
            <h4 className="font-semibold text-orange-900">Conflicting Cruise</h4>
          </div>
          <div className="text-sm space-y-1">
            <p className="font-medium text-orange-800">{conflictingCruise.shipName}</p>
            <p className="text-orange-700">{conflictingCruise.cruiseLine}</p>
            <div className="flex items-center text-orange-600">
              <Calendar className="h-4 w-4 mr-1" />
              <span>
                {formatDate(parseISO(conflictingCruise.departureDate))} - {formatDate(parseISO(conflictingCruise.returnDate))}
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> You can have back-to-back cruises where one ends and another begins on the same day.
          </p>
        </div>
        
        <div className="flex gap-3 pt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
          >
            {cancelText || 'Cancel & Review Dates'}
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition duration-200 font-medium"
          >
            {confirmText || 'Continue Anyway'}
          </button>
        </div>
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CruiseConflictModal;